import { useState, useRef, useEffect } from "react";
import { useTheme } from "../providers/ThemeProvider";
import { RiSendPlaneFill, RiCloseLine, RiRobot2Line, RiUser3Line } from "@remixicon/react";
import axios from "axios";
import { motion, AnimatePresence, useDragControls } from "framer-motion";

interface Message {
    role: "user" | "assistant" | "system";
    content: string;
}

interface AIChatWidgetProps {
    isOpen: boolean;
    onClose: () => void;
}

const API_KEY = import.meta.env.VITE_OPENAI_API_KEY;

export default function AIChatWidget({ isOpen, onClose }: AIChatWidgetProps) {
    const { brandName, primaryColor } = useTheme();
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const dragControls = useDragControls();

    useEffect(() => {
        if (isOpen && messages.length === 0) {
            // Initial message
            setMessages([
                {
                    role: "assistant",
                    content: `Sono il tuo assistente ${brandName || "AI"}, come posso esserti utile?`,
                },
            ]);
        }
    }, [isOpen, brandName, messages.length]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim() || isLoading) return;

        const userMessage: Message = { role: "user", content: input };
        setMessages((prev) => [...prev, userMessage]);
        setInput("");
        setIsLoading(true);

        try {
            // Prepare context: System prompt + last few messages to keep context window manageable
            const systemMessage: Message = {
                role: "system",
                content: `Sei un assistente utile per il brand ${brandName || "Exagon"}. Rispondi in modo cortese e professionale.`,
            };

            const payload = {
                model: "gpt-3.5-turbo", // Or gpt-4o-mini if accessible
                messages: [systemMessage, ...messages, userMessage].map(m => ({ role: m.role, content: m.content })),
                temperature: 0.7,
            };

            const response = await axios.post(
                "https://api.openai.com/v1/chat/completions",
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${API_KEY}`,
                    },
                }
            );

            const assistantMessage = response.data.choices[0].message;
            setMessages((prev) => [...prev, assistantMessage]);
        } catch (error) {
            console.error("Error calling OpenAI:", error);
            setMessages((prev) => [
                ...prev,
                { role: "assistant", content: "Mi dispiace, ho riscontrato un errore nel connettermi al servizio." },
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    drag
                    dragListener={false}
                    dragControls={dragControls}
                    dragMomentum={false}
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 20 }}
                    transition={{ duration: 0.2 }}
                    className="fixed z-50 bottom-24 right-5 w-[350px] md:w-[400px] h-[500px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-100"
                    style={{
                        boxShadow: "0 10px 40px -10px rgba(0,0,0,0.2)",
                    }}
                >
                    {/* Header */}
                    <div
                        onPointerDown={(e) => dragControls.start(e)}
                        className="flex items-center justify-between p-4 text-white cursor-move select-none"
                        style={{ backgroundColor: primaryColor || "#f92f65" }}
                    >
                        <div className="flex items-center gap-2">
                            <RiRobot2Line className="w-6 h-6" />
                            <span className="font-semibold text-lg">{brandName || "AI Assistant"}</span>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1 hover:bg-white/20 rounded full transition-colors"
                            onPointerDown={(e) => e.stopPropagation()}
                        >
                            <RiCloseLine className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 bg-gray-50 space-y-4">
                        {messages.map((msg, index) => {
                            const isAssistant = msg.role === "assistant";
                            return (
                                <div
                                    key={index}
                                    className={`flex ${isAssistant ? "justify-start" : "justify-end"}`}
                                >
                                    <div
                                        className={`max-w-[80%] p-3 rounded-2xl ${isAssistant
                                            ? "bg-white text-gray-800 rounded-tl-none border border-gray-100 shadow-sm"
                                            : "text-white rounded-tr-none shadow-sm"
                                            }`}
                                        style={
                                            !isAssistant
                                                ? { backgroundColor: primaryColor || "#f92f65" }
                                                : {}
                                        }
                                    >
                                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                                    </div>
                                </div>
                            );
                        })}
                        {isLoading && (
                            <div className="flex justify-start">
                                <div className="bg-white p-3 rounded-2xl rounded-tl-none border border-gray-100 shadow-sm">
                                    <div className="flex gap-1">
                                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:0.2s]"></span>
                                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:0.4s]"></span>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div className="p-4 bg-white border-t border-gray-100">
                        <div className="relative flex items-center">
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Scrivi un messaggio..."
                                className="w-full pl-4 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:border-transparent transition-all"
                                style={{
                                    // @ts-ignore
                                    "--tw-ring-color": primaryColor || "#f92f65"
                                }}
                            />
                            <button
                                onClick={handleSend}
                                disabled={!input.trim() || isLoading}
                                className="absolute right-2 p-2 rounded-full text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 active:scale-95"
                                style={{ backgroundColor: primaryColor || "#f92f65" }}
                            >
                                <RiSendPlaneFill className="w-5 h-5" />
                            </button>
                        </div>
                     
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
